import { COMMANDS } from '../../../utils/commonProperties';
import * as THREE from 'three';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCommandMessage, resetOperation } from '../../../redux/operationSlice';
import { clearSelection } from '../../../redux/selectionSlice';
import { useComponentWiring } from '../../../modules/wiring';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { createDoorOpener } from '../../../factory/symbols/createDoorOpener';
import { DoorOpenerCommand } from '../../../modules/undoRedo';
import { store as appStore } from '../../../redux/store';

// Kapı Otomatiği (K.O.) — zil butonu deseni; SADECE interkom hattına bağlanır.
const DOOR_OPENER_GREEN = 0x33cc33;

// Bir wire İnterkom yüküne mi bağlı? (componentId → simpleSupply → machineType/name)
function isInterkomWire(obj, components) {
    const compId = obj.userData?.componentId;
    if (!compId) return false;
    const comp = components.find(c => c.id === compId);
    if (!comp) return false;
    return comp.name === 'İnterkom' || comp.metadata?.machineType === 'İnterkom';
}

const useDoorOpener = (scene, camera, renderer) => {
    const dispatch = useDispatch();
    const { commandType } = useSelector((state) => state.operation);
    const wiringActiveRef = useRef(false);
    const sceneRef = useRef(null);
    const [interkomWireSnapPoints, setInterkomWireSnapPoints] = useState([]);

    const doorOpenerConfig = {
        commandType: COMMANDS.DRAW_DOOR_OPENER,
        itemTypeName: 'doorOpener',
        wireColor: DOOR_OPENER_GREEN,
        allowSpaceKey: false,
        skipItemExecution: true,
        createSymbol: (position, direction, angle, isPreview, itemType, extraData = {}) => {
            return createDoorOpener(null, position, DOOR_OPENER_GREEN, isPreview, direction);
        },
        createCommand: null,
        createFeederData: null,
        createComponentData: null,
        itemTypes: [{ name: 'Kapı Otomatiği', type: 'doorOpener', power: 0 }],
        onKeyDown: null
    };

    useEffect(() => {
        if (commandType !== COMMANDS.DRAW_DOOR_OPENER || !scene || !camera || !renderer) return;
        dispatch(setCommandMessage('Kapı Otomatiği: Önce bir interkom hattı üzerinde nokta seçin.'));
        sceneRef.current = scene;

        // ✅ Sadece interkom hattı vertex'lerini snap noktası olarak topla
        const components = appStore.getState().simpleSupply.components;
        const snapPoints = [];
        scene.traverse((obj) => {
            if (obj.isLine && obj.geometry && isInterkomWire(obj, components)) {
                const positions = obj.geometry.attributes.position;
                obj.updateMatrixWorld(true);
                for (let i = 0; i < positions.count; i++) {
                    const point = new THREE.Vector3(
                        positions.getX(i), positions.getY(i), positions.getZ(i)
                    ).applyMatrix4(obj.matrixWorld);
                    snapPoints.push({ position: point, type: 'interkomWireVertex', wireObject: obj });
                }
            }
        });

        setInterkomWireSnapPoints(snapPoints);
        wiringActiveRef.current = true;

        return () => {
            wiringActiveRef.current = false;
        };
    }, [commandType, scene, camera, renderer, dispatch]);

    const handleDoorOpenerPlace = () => null;

    const handleDoorOpenerRemove = (doorOpener) => {
        if (!doorOpener) return;
        sceneRef.current.remove(doorOpener);
        doorOpener.traverse(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
        });
    };

    const handleWireEnd = (vertices, doorOpeners, source, doorOpener, tempLineUUID, wireMarkers) => {
        const branchID = generateUniqueId('branchDoorOpener');
        const wireID = generateUniqueId('wireDoorOpener');
        const koId = generateUniqueId('doorOpener');

        const lastVertex = vertices[vertices.length - 1];
        const prevVertex = vertices[vertices.length - 2] || vertices[vertices.length - 1];
        const direction = new THREE.Vector3().subVectors(lastVertex, prevVertex);
        const koAngle = Math.atan2(direction.y, direction.x);

        const cmd = new DoorOpenerCommand('ADD', {
            buttonId: koId,
            buttonPosition: [lastVertex.x, lastVertex.y, lastVertex.z],
            buttonAngle: koAngle,
            wireId: wireID,
            branchID: branchID,
            vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
            color: DOOR_OPENER_GREEN
        });

        cmd.execute(scene, dispatch);

        dispatch(setCommandMessage('Kapı Otomatiği yerleştirildi.'));
        dispatch(resetOperation());
        dispatch(clearSelection());
        wiringActiveRef.current = false;

        return { wireId: wireID, branchId: branchID };
    };

    useComponentWiring(
        scene,
        camera,
        renderer,
        interkomWireSnapPoints, // ✅ Sadece interkom hattına snap
        {
            ...doorOpenerConfig,
            enabled: () => wiringActiveRef.current,
            onWireEnd: handleWireEnd,
            onSocketPlace: handleDoorOpenerPlace,
            onItemAdd: () => {},
            onSocketRemove: handleDoorOpenerRemove,
            onPreviewUpdate: () => {},
            onWireCancel: () => {
                wiringActiveRef.current = false;
                dispatch(resetOperation());
                dispatch(setCommandMessage('Kapı Otomatiği iptal edildi.'));
            }
        }
    );
};

export default useDoorOpener;
